import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./BackButton.css";
import { ROUTES } from "../../utils/appRoutes";

/**
 * The phone back control, the same one the mobile app uses: a green arrow on a
 * dark ringed disc, top left, scrolling away with the page.
 *
 * Follows the app's rule. The two tab pages, Home and Profile, have none;
 * every other page has one. Phone widths only (see BackButton.css), since on
 * wider screens the navbar is there to get around with.
 */

// Reached from the bottom bar, so there is nothing to go back to.
const TAB_ROOTS = new Set([ROUTES.HOME, ROUTES.PROFILE]);

// Sign-in pages go home rather than back: back from the login page could land
// on the register page you came from, and the way out should be the way out.
const AUTH_PATHS = new Set([
  ROUTES.LOGIN,
  ROUTES.REGISTER,
  ROUTES.CONFIRM,
  ROUTES.FORGOT_PASSWORD,
  ROUTES.RESET_PASSWORD,
]);

function BackButton() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (TAB_ROOTS.has(pathname)) return null;

  const handleBack = () => {
    // React Router counts the entries it pushed; 0 means this page was opened
    // directly (a shared link, a refresh), so there is no back to go to.
    const canGoBack = (window.history.state?.idx ?? 0) > 0;
    if (AUTH_PATHS.has(pathname) || !canGoBack) {
      navigate(ROUTES.HOME);
      return;
    }
    navigate(-1);
  };

  return (
    <button type="button" className="back-button" onClick={handleBack} aria-label="Go back">
      {/* Ionicons 7 "arrow-back", as in the app. */}
      <svg viewBox="0 0 512 512" aria-hidden="true" focusable="false">
        <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="48" d="M244 400L100 256l144-144M120 256h292" />
      </svg>
    </button>
  );
}

export default BackButton;
